import { useEffect, useRef, useState } from "react";
import type { AuthChangeEvent } from "@supabase/supabase-js";
import type { Location } from "react-router-dom";
import type { AuthRedirectReason } from "@/lib/auth-transition-logger";

export function getAuthRedirectReason(lastAuthEvent: AuthChangeEvent | null): AuthRedirectReason {
  if (lastAuthEvent === "SIGNED_OUT") {
    return "manual_logout";
  }
  if (lastAuthEvent === "TOKEN_REFRESHED") {
    return "session_expired";
  }
  return "not_authenticated";
}

export function hasExplicitSignedOutState(
  isAuthenticated: boolean,
  lastAuthEvent: AuthChangeEvent | null,
): boolean {
  return !isAuthenticated && lastAuthEvent === "SIGNED_OUT";
}

interface InitialSessionErrorRecoveryInput {
  isAuthenticated: boolean;
  isAuthLoading: boolean;
  hasSessionError: boolean;
  lastAuthEvent: AuthChangeEvent | null;
  rehydrateSession: () => Promise<boolean>;
}

/**
 * Auth-js can emit INITIAL_SESSION with a null session after its own restore
 * fails. Retry once before showing the recovery boundary: a successful
 * anonymous retry can redirect to sign-in, while a persistent error remains
 * visible to the user instead of being mistaken for a logout.
 */
export function useInitialSessionErrorRecovery({
  isAuthenticated,
  isAuthLoading,
  hasSessionError,
  lastAuthEvent,
  rehydrateSession,
}: InitialSessionErrorRecoveryInput): boolean {
  const [hasTriedRecovery, setHasTriedRecovery] = useState(false);
  const [isRecovering, setIsRecovering] = useState(false);
  const isMountedRef = useRef(true);
  const shouldRecover =
    !isAuthenticated &&
    !isAuthLoading &&
    hasSessionError &&
    lastAuthEvent === "INITIAL_SESSION" &&
    !hasTriedRecovery;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!shouldRecover) {
      return;
    }

    setIsRecovering(true);

    void (async () => {
      try {
        await rehydrateSession();
      } finally {
        if (isMountedRef.current) {
          setHasTriedRecovery(true);
          setIsRecovering(false);
        }
      }
    })();
  }, [rehydrateSession, shouldRecover]);

  return isRecovering || shouldRecover;
}

export function getAuthRedirectTarget(location: Pick<Location, "pathname" | "search" | "hash">): string {
  const returnTo = `${location.pathname}${location.search}${location.hash}`;
  return returnTo && returnTo !== "/" && returnTo !== "/auth"
    ? `/auth?redirect=${encodeURIComponent(returnTo)}`
    : "/auth";
}
